import { useEffect, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUp, Trash2, Camera } from "lucide-react";
import OnlineRatings from "@/components/OnlineRatings";
import RatingCards from "@/components/RatingCards";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [ratingsForm, setRatingsForm] = useState({});
  const [onlineRatings, setOnlineRatings] = useState(null);
  const [savingRatings, setSavingRatings] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const [avatarCacheBust, setAvatarCacheBust] = useState(Date.now());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);
  useEffect(() => { setData(user?.athlete); }, [user]);
  useEffect(() => {
    if (!data) return;
    setRatingsForm({
      chess_com_username: data.chess_com_username || "",
      lichess_username: data.lichess_username || "",
      fsx_player_id: data.fsx_player_id == null ? "" : String(data.fsx_player_id),
    });
  }, [data]);

  if (!data) return <AppLayout kind="athlete"><div className="text-slate-300">Carregando…</div></AppLayout>;

  const avatarUrl = data.avatar_file_id
    ? `${API}/athletes/${data.id}/avatar?v=${avatarCacheBust}`
    : null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/athletes/${data.id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setAvatarCacheBust(Date.now());
      toast.success("Foto publicada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no envio");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    if (!window.confirm("Remover a foto atual?")) return;
    try {
      await api.delete(`/athletes/${data.id}/avatar`);
      await refresh();
      setAvatarCacheBust(Date.now());
      toast.success("Foto removida");
    } catch { toast.error("Erro"); }
  };

  const saveRatings = async (e) => {
    e.preventDefault();
    const fsxId = ratingsForm.fsx_player_id.trim() === "" ? null : Number(ratingsForm.fsx_player_id);
    if (fsxId !== null && (!Number.isInteger(fsxId) || fsxId <= 0)) {
      toast.error("O ID FSX deve ser um número inteiro maior que zero");
      return;
    }
    setSavingRatings(true);
    try {
      const { data: updated } = await api.put("/athletes/me/ratings", {
        chess_com_username: ratingsForm.chess_com_username.trim() || null,
        lichess_username: ratingsForm.lichess_username.trim() || null,
        fsx_player_id: fsxId,
      });
      setData(updated);
      await refresh();
      toast.success("Dados de xadrez atualizados");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Nao foi possivel salvar os dados");
    } finally {
      setSavingRatings(false);
    }
  };

  return (
    <AppLayout kind="athlete">
      <div className="mx-auto w-full max-w-[1500px]">
        <h1 className="mb-8 text-3xl font-semibold text-white sm:text-4xl">Perfil do Atleta</h1>

        <div className="grid gap-6 xl:grid-cols-[minmax(300px,0.9fr)_minmax(0,1.55fr)] xl:items-start">
          <div className="flex min-w-0 flex-col gap-6">
            <Card className="bg-transparent gsx-card-dark p-6 text-center sm:p-7">
              <div className="relative mx-auto h-24 w-24">
                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-amber-500/40 bg-amber-500/20">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={data.name} className="h-full w-full object-cover" data-testid="profile-avatar-img" />
                  ) : (
                    <span className="text-3xl font-bold text-amber-300">{(data.name || "?").slice(0, 1)}</span>
                  )}
                </div>
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                  aria-label="Alterar foto do perfil" data-testid="profile-avatar-upload-btn"
                  className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-[#071A33] shadow-md transition hover:bg-amber-300 disabled:opacity-60">
                  <Camera className="h-4 w-4" />
                </button>
              </div>
              <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={upload} className="hidden" data-testid="profile-avatar-input" />

              <div className="mt-5 text-lg font-semibold text-white">{data.name}</div>
              <div className="mt-1 text-xs text-slate-400">{data.category} • {data.city}</div>
              <div className="mt-2 text-xs text-amber-300">Membro desde {data.joined_at}</div>

              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}
                  className="bg-amber-500 font-semibold text-[#071A33] hover:bg-amber-400">
                  <FileUp className="mr-1 h-3.5 w-3.5" /> {uploading ? "Enviando…" : (avatarUrl ? "Trocar foto" : "Enviar foto")}
                </Button>
                {avatarUrl && (
                  <Button size="sm" variant="destructive" onClick={removeAvatar} aria-label="Remover foto" data-testid="profile-avatar-remove-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="mt-2 text-[11px] text-slate-500">JPG/PNG/WEBP até 5 MB</div>
            </Card>

            <Card className="bg-transparent gsx-card-dark p-6 sm:p-7">
              <h2 className="mb-4 font-semibold text-white">Perfis de xadrez</h2>
              <form onSubmit={saveRatings} className="grid gap-4 sm:grid-cols-2" data-testid="athlete-ratings-form">
                <ProfileField label="Username Chess.com" value={ratingsForm.chess_com_username} onChange={value => setRatingsForm(form => ({ ...form, chess_com_username: value }))} />
                <ProfileField label="Username Lichess" value={ratingsForm.lichess_username} onChange={value => setRatingsForm(form => ({ ...form, lichess_username: value }))} />
                <ProfileField label="ID do jogador FSX" type="number" min="1" step="1" value={ratingsForm.fsx_player_id} onChange={value => setRatingsForm(form => ({ ...form, fsx_player_id: value }))} />
                <Button type="submit" disabled={savingRatings} data-testid="athlete-ratings-save" className="bg-amber-500 font-semibold text-[#071A33] hover:bg-amber-400 sm:col-span-2">
                  {savingRatings ? "Salvando..." : "Salvar dados de xadrez"}
                </Button>
              </form>
            </Card>
          </div>

          <div className="flex min-w-0 flex-col gap-6">
            <Card className="bg-transparent gsx-card-dark p-6 sm:p-7">
              <h2 className="mb-4 font-semibold text-white">Ratings online</h2>
              <OnlineRatings athleteId={data.id} chessComUsername={data.chess_com_username} lichessUsername={data.lichess_username} fsxPlayerId={data.fsx_player_id} onData={setOnlineRatings} />
              <div className="mt-6 border-t border-white/10 pt-5">
                <h2 className="mb-4 font-semibold text-white">Resumo dos ratings</h2>
                <RatingCards ratingCbx={data.rating_cbx} ratingFide={data.rating_fide} onlineRatings={onlineRatings} />
              </div>
            </Card>

            <Card className="bg-transparent gsx-card-dark p-6 sm:p-7">
              <h2 className="mb-4 font-semibold text-white">Dados pessoais</h2>
              <div className="grid gap-4 text-sm sm:grid-cols-2">
                <Info label="Escola" value={data.school} />
                <Info label="Cidade" value={data.city} />
                <Info label="Data de nascimento" value={data.birth_date} />
                <Info label="Email" value={data.email} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
function ProfileField({ label, value, onChange, type = "text", min, step }) {
  const id = `athlete-profile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return <div className="space-y-2">
    <Label htmlFor={id} className="text-slate-300">{label}</Label>
    <Input id={id} type={type} min={min} step={step} value={value ?? ""} onChange={event => onChange(event.target.value)}
      className="bg-[#0D2A52] border-amber-500/20 text-white" />
  </div>;
}
function Info({ label, value }) {
  return <div className="min-w-0"><div className="text-xs text-slate-400 uppercase tracking-widest">{label}</div><div className="break-words text-slate-100">{value || "—"}</div></div>;
}
